import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { ValidationModal } from "@/components/ui/ValidationModal";
import { applicantValidationModalProps } from "@/features/applicant/constants/applicantDialogProps";
import { NOTIFICATION_TITLES } from "@/constants/notifications";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent';
import type {
  ValidationResult,
  ValidatorFunction,
  SaverFunction,
  TabValidationContextType,
} from "@/features/applicant/types/validation";

const TabValidationContext = createContext<TabValidationContextType | undefined>(
    undefined,
);

export function TabValidationProvider({ children }: { children: ReactNode }) {
    const [validators, setValidators] = useState<Record<string, ValidatorFunction>>({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    // Saver registry is a ref (not state): savers are invoked imperatively on tab
    // change, never rendered, so we avoid re-render churn on every keystroke.
    const saversRef = useRef<Record<string, SaverFunction>>({});

    const registerValidator = useCallback((name: string, validator: ValidatorFunction) => {
        setValidators((prev) => ({ ...prev, [name]: validator }));
    }, []);

    const unregisterValidator = useCallback((name: string) => {
        setValidators((prev) => {
            const { [name]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const validate = useCallback((): ValidationResult => {
        const results = Object.values(validators).map((v) => v());
        const invalidResult = results.find((r) => !r.isValid);

        if (invalidResult) {
            setModalMessage(invalidResult.message || APPLICANT_CONTENT.errors.invalidInputData);
            setIsModalOpen(true);
            return invalidResult;
        }

        return { isValid: true };
    }, [validators]);

    const showValidationModal = useCallback((message: string) => {
        setModalMessage(message);
        setIsModalOpen(true);
    }, []);

    const registerSaver = useCallback((name: string, saver: SaverFunction) => {
        saversRef.current[name] = saver;
    }, []);

    const unregisterSaver = useCallback((name: string) => {
        delete saversRef.current[name];
    }, []);

    const saveCurrent = useCallback(async (): Promise<void> => {
        // Only the active tab's saver is registered at any time (tabs unregister on
        // unmount), so this runs the current tab's save. Each saver self-guards on
        // dirty state, so this is a no-op when nothing changed.
        const savers = Object.values(saversRef.current);
        for (const saver of savers) {
            await saver();
        }
    }, []);

    return (
        <TabValidationContext.Provider
            value={{ registerValidator, unregisterValidator, validate, showValidationModal, registerSaver, unregisterSaver, saveCurrent }}
        >
            {children}
            <ValidationModal
                {...applicantValidationModalProps}
                isOpen={isModalOpen}
                title={NOTIFICATION_TITLES.missingFields}
                message={modalMessage}
                onClose={() => setIsModalOpen(false)}
            />
        </TabValidationContext.Provider>
    );
}

export function useTabValidation() {
    const context = useContext(TabValidationContext);
    if (context === undefined) {
        throw new Error("useTabValidation must be used within a TabValidationProvider");
    }
    return context;
}

interface UseTabAutoSaveOptions {
    /** Unique tab name (same key used for registerValidator). */
    name: string;
    /**
     * A JSON-serialisable snapshot of the tab's EDITABLE fields. The hook captures
     * a baseline of this on load and treats the tab as dirty when it differs.
     */
    snapshot: unknown;
    /** Truthy once the tab's data has loaded (e.g. applicationData?.applicant_id). Baseline is captured/re-captured on change. */
    loadedKey: unknown;
    /** Persists the current tab WITHOUT navigating. Called only when dirty + editable. */
    save: () => Promise<void>;
    /** When true (read-only application), auto-save is disabled — mirrors Angular's isAllowEdit gate. */
    isReadOnly: boolean;
}

/**
 * Registers a dirty-guarded, navigation-free saver for a tab so the stepper can
 * auto-save the current tab on tab change (Angular `submitForm('fromtab')` parity).
 *
 * Dirty detection compares the current `snapshot` against a baseline captured when
 * the tab's data loads (and re-captured after each successful auto-save). Because the
 * snapshot is the SAME representation both times, any real edit is detected and a
 * field that is not in the snapshot simply is not persisted — so this never causes
 * silent data loss for fields the tab actually saves.
 */
export function useTabAutoSave({ name, snapshot, loadedKey, save, isReadOnly }: UseTabAutoSaveOptions) {
    const { registerSaver, unregisterSaver } = useTabValidation();
    const snapshotStr = JSON.stringify(snapshot ?? null);

    const baselineRef = useRef<string | null>(null);
    const needsBaselineRef = useRef(true);
    // Keep the latest snapshot/save/read-only in a ref so the registered saver
    // (registered once) always sees current values without re-registering per keystroke.
    const latestRef = useRef({ snapshotStr, save, isReadOnly });
    latestRef.current = { snapshotStr, save, isReadOnly };

    // Mark for (re)baseline whenever a fresh application loads.
    useEffect(() => {
        needsBaselineRef.current = true;
    }, [loadedKey]);

    // Capture the baseline once the tab's state reflects the loaded data.
    useEffect(() => {
        if (needsBaselineRef.current && loadedKey) {
            baselineRef.current = snapshotStr;
            needsBaselineRef.current = false;
        }
    }, [snapshotStr, loadedKey]);

    // Register a stable saver wrapper once; it reads the latest values via refs.
    useEffect(() => {
        const saver: SaverFunction = async () => {
            const { snapshotStr: current, save: doSave, isReadOnly: ro } = latestRef.current;
            if (ro) return;
            if (baselineRef.current === null) return; // not loaded yet
            if (current === baselineRef.current) return; // pristine — nothing to save
            await doSave();
            baselineRef.current = current; // mark clean after a successful save
        };
        registerSaver(name, saver);
        return () => unregisterSaver(name);
    }, [name, registerSaver, unregisterSaver]);
}
